import React, { useState, useCallback } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
    Box, Typography, Tabs, Tab, styled, IconButton, Tooltip,
} from '@mui/material';
import {
    Dashboard as OverviewIcon,
    FormatListBulleted as AllListIcon,
    Settings as SettingsIcon,
    HistoryEdu as AuditIcon,
    Assessment as ReportsIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import AllTimesheetsList from './allTimesheetsList';
import TimesheetWorkflowConfig from './timesheetWorkflowConfig';
import TimesheetAuditLogs from './timesheetAuditLogs';
import TimesheetReportsExport from './timesheetReportsExport';
import HRTimesheetOverview from './hrTimesheetOverview';

const StyledTabs = styled(Tabs)(() => ({
    backgroundColor: '#fff',
    borderRadius: 12,
    marginBottom: 0,
    padding: '4px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    border: '1px solid #e2e8f0',
    '& .MuiTabs-indicator': { display: 'none' },
    '& .MuiTab-root': {
        fontWeight: 600,
        fontSize: 13,
        color: '#64748b',
        textTransform: 'none',
        minHeight: 40,
        borderRadius: 8,
        margin: '0 2px',
        '&.Mui-selected': {
            color: '#6366f1',
            backgroundColor: '#eef2ff',
        },
    },
}));

export default function HRTimesheetManagement() {
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState(() => {
        const fromUrl = parseInt(searchParams.get('tab'), 10);
        return Number.isInteger(fromUrl) ? fromUrl : (location.state?.returnTab ?? 0);
    });
    const [refreshKey, setRefreshKey] = useState(0);

    const handleTabChange = useCallback((v, status = null) => {
        setActiveTab(v);
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('tab', String(v));
            if (status) {
                next.set('status', status);
            } else {
                next.delete('status');
            }
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    return (
        <Box>
            <Box sx={{ mb: 2.5, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', mb: 0.25 }}>
                        Timesheet Management
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                        Company-wide timesheet oversight, approval configuration, audit logs, and reporting
                    </Typography>
                </Box>
                <Tooltip title="Refresh">
                    <IconButton onClick={() => setRefreshKey(k => k + 1)} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
                        <RefreshIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>

            <StyledTabs
                value={activeTab}
                onChange={(_, v) => handleTabChange(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ mb: 2.5 }}
            >
                <Tab icon={<OverviewIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Overview" />
                <Tab icon={<AllListIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="All Timesheets" />
                <Tab icon={<SettingsIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Workflow Configuration" />
                <Tab icon={<AuditIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Audit Logs" />
                <Tab icon={<ReportsIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Reports & Export" />
            </StyledTabs>

            {activeTab === 0 && <HRTimesheetOverview onTabChange={handleTabChange} refreshKey={refreshKey} />}
            {activeTab === 1 && <AllTimesheetsList refreshKey={refreshKey} />}
            {activeTab === 2 && <TimesheetWorkflowConfig refreshKey={refreshKey} />}
            {activeTab === 3 && <TimesheetAuditLogs refreshKey={refreshKey} />}
            {activeTab === 4 && <TimesheetReportsExport refreshKey={refreshKey} />}
        </Box>
    );
}
